import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2, X, Store, Bike, Car, HelpCircle } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import ProspectionTab from "./ProspectionTab";

// Onglet Prospection Chauffeurs — réutilise ProspectionTab en surchargeant les endpoints & rendu.
// L'ajout manuel d'un chauffeur rencontré ouvre une modale.
export default function DriverProspectionTab() {
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", commune: "Bruxelles", vehicle_type: "taxi", note: "" });
  const [refreshKey, setRefreshKey] = useState(0);

  const addDriver = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) return toast.error("Nom requis (min. 2 caractères).");
    setSaving(true);
    try {
      await api.post("/agent/driver-prospects", form);
      toast.success("Chauffeur ajouté ✅");
      setShowAdd(false);
      setForm({ name: "", phone: "", commune: "Bruxelles", vehicle_type: "taxi", note: "" });
      setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="flex justify-end mb-2">
        <Button data-testid="drv-add-open" onClick={() => setShowAdd(true)}
          className="h-10 rounded-lg bg-[#F26419] hover:bg-[#D94D0E] text-white font-semibold text-sm px-4 inline-flex items-center gap-2">
          <UserPlus className="w-4 h-4" /> Ajouter un chauffeur rencontré
        </Button>
      </div>

      <ProspectionTab
        key={refreshKey}
        title="Recrutement Chauffeurs"
        icon={<Bike className="w-5 h-5 text-[#F26419]" />}
        endpointBase="/agent/driver-prospects"
        importPath="/admin/driver-prospects/import-brussels"
        emailSubject="Recrutement chauffeur Nold Go — Bruxelles"
        contactMessage={(p) => {
          const isSchool = p.hotspot_kind === "driving_school";
          const isTaxi = p.hotspot_kind === "taxi_company" || p.vehicle_type === "taxi";
          const isRental = p.hotspot_kind === "car_rental";
          const isCyclist = p.vehicle_type === "velo" || p.vehicle_type === "moto";
          if (isSchool) {
            return `Bonjour ${p.name}, je suis agent Nold Go (VTC à Bruxelles). Auriez-vous des élèves fraîchement diplômés du permis B intéressés par des courses rémunérées en flexible ? Merci !`;
          }
          if (isTaxi) {
            return `Bonjour ${p.name}, je suis agent Nold Go. Nous cherchons des chauffeurs taxi expérimentés pour compléter leurs revenus sur notre plateforme VTC (paiement sécurisé, faible commission). Pourriez-vous transmettre à vos chauffeurs ?`;
          }
          if (isRental) {
            return `Bonjour ${p.name}, je suis agent Nold Go. Vos clients loueurs pourraient être intéressés par notre plateforme VTC pour rentabiliser leur location. Puis-je vous en dire plus ?`;
          }
          if (isCyclist) {
            return `Bonjour ${p.name}, je suis agent Nold Go. Nous recrutons des livreurs à vélo/moto à Bruxelles pour la livraison de repas (horaires flexibles, paiement sécurisé). Intéressé(e) ?`;
          }
          return `Bonjour ${p.name}, je suis agent Nold Go. Nous recrutons des chauffeurs VTC à Bruxelles (paiement sécurisé, faible commission). Auriez-vous 2 minutes pour en discuter ?`;
        }}
        extraFilters={(filters, setFilters) => (
          <select data-testid="drv-vehicle-filter" value={filters.vehicle_type || ""}
            onChange={(e) => setFilters((f) => ({ ...f, vehicle_type: e.target.value }))}
            className="h-10 rounded-lg border-2 border-[#E2DFD8] px-3 text-sm bg-white">
            <option value="">Tous véhicules</option>
            <option value="taxi">🚕 Taxi</option>
            <option value="vtc">🚗 VTC</option>
            <option value="velo">🚴 Vélo</option>
            <option value="moto">🏍 Moto</option>
            <option value="unknown">❓ Non défini</option>
          </select>
        )}
        renderItemBadge={(p) => {
          if (p.is_hotspot) {
            const kindLabel = {
              driving_school: "🎓 Auto-école",
              taxi_company:   "🚕 Cie taxi",
              car_rental:     "🚗 Loueur",
              car_dealer:     "🏬 Concess. auto",
              car_repair:     "🔧 Garage",
              fuel_station:   "⛽ Station",
              // legacy
              taxi_stand:     "🚕 Station taxi",
              train_station:  "🚉 Gare",
              tram_stop:      "🚋 Tram",
              metro_station:  "🚇 Métro",
              bus_station:    "🚌 Bus",
              villo_station:  "🚲 Villo!",
            }[p.hotspot_kind] || "📍 Zone";
            return <span className="shrink-0 rounded-full text-[10px] font-bold px-2 py-0.5 mt-0.5 bg-[#FFF3E6] text-[#7A3A00]">{kindLabel}</span>;
          }
          const vt = { taxi: "🚕 TAXI", vtc: "🚗 VTC", velo: "🚴 VÉLO", moto: "🏍 MOTO" }[p.vehicle_type] || "❓ CHAUFFEUR";
          return <span className="shrink-0 rounded-full text-[10px] font-bold px-2 py-0.5 mt-0.5 bg-[#DCFCE7] text-[#166534]">{vt}</span>;
        }}
      />

      {showAdd && (
        <div data-testid="drv-add-modal" className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-3xl p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-head font-black text-xl">Chauffeur rencontré</h3>
              <button onClick={() => setShowAdd(false)} className="text-[#75807A] hover:text-[#1A1A1A]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-[#75807A]">Nom *</label>
                <Input data-testid="drv-add-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="ex. Kamga Bertrand" className="h-11 rounded-lg border-2 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold text-[#75807A]">Téléphone</label>
                  <Input data-testid="drv-add-phone" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+32 ..." className="h-11 rounded-lg border-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#75807A]">Commune</label>
                  <Input data-testid="drv-add-commune" value={form.commune}
                    onChange={(e) => setForm({ ...form, commune: e.target.value })}
                    placeholder="Bruxelles" className="h-11 rounded-lg border-2 mt-1" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#75807A]">Type de véhicule</label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {[
                    { v: "taxi", l: "🚕 Taxi" }, { v: "vtc", l: "🚗 VTC" },
                    { v: "velo", l: "🚴 Vélo" }, { v: "moto", l: "🏍 Moto" },
                  ].map((o) => (
                    <button key={o.v} data-testid={`drv-add-vt-${o.v}`}
                      onClick={() => setForm({ ...form, vehicle_type: o.v })}
                      className={`h-11 rounded-lg text-xs font-bold border-2 transition-colors ${form.vehicle_type === o.v ? "bg-[#F26419] text-white border-[#F26419]" : "bg-white text-[#1A1A1A] border-[#E2DFD8] hover:border-[#F26419]"}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#75807A]">Note (optionnel)</label>
                <Input data-testid="drv-add-note" value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Rencontré à la gare du Nord..." className="h-11 rounded-lg border-2 mt-1" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => setShowAdd(false)} className="flex-1 h-11 rounded-lg bg-white border-2 border-[#E2DFD8] text-[#1A1A1A] font-semibold">Annuler</Button>
              <Button data-testid="drv-add-save" onClick={addDriver} disabled={saving}
                className="flex-1 h-11 rounded-lg bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold inline-flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Ajouter
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
